import { Button } from "@/components/ui/button";
import { config } from "@/config";
import { useCurrentView } from "@/hooks/use-current-view";
import { useAppState, useApp } from "@/store/useApp";
import { useFlow } from "@frigade/react";
import { AlertCircle, Loader2, Send } from "lucide-react";
import { useRef } from "react";
import { toast } from "sonner";
import { GenerateReportTrigger } from "./GenerateReportTrigger";
import { NextSubmissionButton } from "./NextSubmissionButton";
import { useApi } from "@/hooks/use-api";
import { useAppDispatch } from "@/store/useApp";

export const FeedbackActions = () => {
  const grading = useAppState((state) => state.grading);
  const currentDocument = useAppState((state) => state.currentDocument);
  const isPublishingFeedback = useAppState((state) => state.isPublishingFeedback);
  const { flow } = useFlow(config.frigade.flows.shareFeedbackHint);
  const hasGrade = grading?.grade && grading.grade.trim() !== "";
  const currentView = useCurrentView();
  const { publishFeedback } = useApi();
  const dispatch = useAppDispatch();
  const { state } = useApp();
  const publishingDocumentIdRef = useRef<string | null>(null);
  
  const isPublishing = isPublishingFeedback;

  const handleShareFeedbackClick = async () => {
    console.log("handleShareFeedbackClick");
    if (!currentDocument || !grading) return;

    if (!hasGrade) {
      toast.error("Grade required", {
        description: "A final grade must be assigned before sharing feedback.",
        icon: <AlertCircle className="h-4 w-4" />,
        position: "bottom-right",
      });
      return;
    }

    if (!grading?.feedback?.summary) {
      return;
    }

    if (!flow?.isCompleted) {
      flow?.complete();
    }

    // Store the document ID we're publishing for
    const publishingDocumentId = currentDocument.id;
    publishingDocumentIdRef.current = publishingDocumentId;

    // Dispatch action to set publishing state
    dispatch({ type: "SET_IS_PUBLISHING_FEEDBACK", payload: true });

    try {
      await publishFeedback(
        publishingDocumentId,
        grading.feedback.summary || "",
        grading.id
      );

      // Check if we're still viewing the same document after the API call completes
      // The publishFeedback API call already dispatches UPDATE_FEEDBACK which sets isPublishingFeedback to false
      // We need to check the current state at this point in time
      if (state.currentDocument?.id === publishingDocumentId) {
        toast.success("Feedback successfully published", {
          position: "bottom-right",
        });
      }
    } catch {
      dispatch({ type: "SET_IS_PUBLISHING_FEEDBACK", payload: false });
      toast.error("Failed to publish feedback", {
        description: "Please try again later.",
        icon: <AlertCircle className="h-4 w-4" />,
        position: "bottom-right",
      });
    } finally {
      publishingDocumentIdRef.current = null;
    }
  };

  if (currentView !== "grading") {
    return <GenerateReportTrigger currentView={currentView} />;
  }

  return (
    <>
      <NextSubmissionButton />
      {grading?.feedback?.status !== "published" && (
        <Button
          id="hint-share-feedback"
          className="mr-6"
          onClick={handleShareFeedbackClick}
          disabled={isPublishing}
        >
          {isPublishing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Publishing feedback
            </>
          ) : (
            <>
              Publish feedback
              <Send className="h-4 w-4" />
            </>
          )}
        </Button>
      )}
    </>
  );
};
